import {
  ContentStatus,
  ExploreBlock,
  ExploreBlockType,
  Gender,
  UpsertExploreBlockRequest,
} from '@addmile/backend-client';

import { BlockFormData } from './components/BlockForm/schema';

export function getErrorPosition(json: string, error: unknown): string | null {
  if (error instanceof SyntaxError) {
    const match = error.message.match(/at position (\d+)/);
    if (!match) return null;

    const pos = parseInt(match[1], 10);
    const lines = json.substring(0, pos).split('\n');
    const line = lines.length;
    const column = lines[lines.length - 1].length + 1;
    return `Line ${line}, Column ${column}`;
  }
  return null;
}

/**
 * Normalizes gender value to ensure it's a valid Gender enum value
 */
const normalizeGender = (gender: Gender | undefined): Gender => {
  return gender === Gender.Male || gender === Gender.Female ? gender : Gender.Male;
};

/**
 * Transforms Block (API response) into BlockFormData (form state)
 */
export const transformBlockToFormData = (
  block: ExploreBlock,
  slug?: string,
): Partial<BlockFormData> => {
  const normalizedGender = normalizeGender(block.author?.gender);

  const baseFormData: Partial<BlockFormData> = {
    slug: block.slug || slug || '',
    title: block.title || '',
    type: block.type as BlockFormData['type'],
    imageUrl: block.imageUrl || '',
    intro: block.intro || '',
    author: block.author
      ? {
          name: block.author.name || '',
          title: block.author.title || '',
          photoUrl: block.author.photoUrl || '',
          gender: normalizedGender,
        }
      : {
          name: '',
          title: '',
          photoUrl: '',
          gender: Gender.Male,
        },
    showFeedback: block.showFeedback || false,
    status: block.status || ContentStatus.Offline,
    personalizations: block.personalizations || [],
    language: (block.language as 'en' | 'es') || 'en',
    details:
      typeof block.details === 'string' ? block.details : JSON.stringify(block.details || {}),
  };

  if (block.type === ExploreBlockType.Video) {
    const formData: Partial<BlockFormData> = {
      ...baseFormData,
      videoUrl: block.videoUrl || '',
    };
    return formData;
  } else {
    const generateAudio =
      'generateAudio' in block
        ? ((block as { generateAudio?: boolean }).generateAudio ?? true)
        : true;

    const formData: Partial<BlockFormData> = {
      ...baseFormData,
      ...(block.type === ExploreBlockType.Article ? { generateAudio } : {}),
      content: JSON.stringify(block.content || [[]], null, 2),
    };
    return formData;
  }
};

/**
 * Transforms Partial<UpsertBlockRequest> into BlockFormData (form state)
 */
export const transformBlockRequestToFormData = (
  blockRequestData: Partial<UpsertExploreBlockRequest>,
  slug?: string,
): Partial<BlockFormData> => {
  const normalizedGender = normalizeGender(blockRequestData.author?.gender);

  const baseFormData: Partial<BlockFormData> = {
    slug: slug || '',
    title: blockRequestData.title || '',
    type: blockRequestData.type as BlockFormData['type'],
    imageUrl: blockRequestData.imageUrl || '',
    intro: blockRequestData.intro || '',
    author: blockRequestData.author
      ? {
          name: blockRequestData.author.name || '',
          title: blockRequestData.author.title || '',
          photoUrl: blockRequestData.author.photoUrl || '',
          gender: normalizedGender,
        }
      : { name: '', title: '', photoUrl: '', gender: Gender.Male },
    showFeedback: blockRequestData.showFeedback || false,
    status: blockRequestData.status || ContentStatus.Offline,
    personalizations: blockRequestData.personalizations || [],
    language: (blockRequestData.language as 'en' | 'es') || 'en',
    details:
      typeof blockRequestData.details === 'string'
        ? blockRequestData.details
        : JSON.stringify(blockRequestData.details || {}),
  };

  if (blockRequestData.type === ExploreBlockType.Video) {
    return {
      ...baseFormData,
      videoUrl: blockRequestData.videoUrl || '',
    };
  } else {
    return {
      ...baseFormData,
      ...(blockRequestData.type === ExploreBlockType.Article
        ? { generateAudio: blockRequestData.generateAudio ?? true }
        : {}),
      content: JSON.stringify(blockRequestData.content || [[]], null, 2),
    };
  }
};

/**
 * Helper function to check if a value is considered "empty" and should be omitted
 */
const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    // Check if it's an array containing only empty arrays like [[]]
    return value.every((item) => Array.isArray(item) && item.length === 0);
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
};

/**
 * Helper function to check if author object has all empty fields
 */
const isAuthorEmpty = (author: {
  name: string;
  title: string;
  photoUrl: string;
  gender: Gender;
}): boolean => {
  return !author.name && !author.title && !author.photoUrl;
};

export const transformBlockFormDataToRequest = (
  blockFormData: BlockFormData,
): UpsertExploreBlockRequest => {
  let parsedContent;
  try {
    parsedContent =
      'content' in blockFormData && blockFormData.content
        ? JSON.parse(blockFormData.content)
        : undefined;
  } catch (error) {
    console.error('Failed to parse content JSON:', error);
    throw new Error('Invalid JSON in content field');
  }

  let parsedDetails;
  if (blockFormData.details) {
    try {
      parsedDetails = JSON.parse(blockFormData.details);
    } catch (error) {
      console.error('Failed to parse details JSON:', error);
      throw new Error('Invalid JSON in details field');
    }
  }

  let authorData;
  if ('author' in blockFormData && blockFormData.author) {
    const author = {
      name: blockFormData.author.name || '',
      title: blockFormData.author.title || '',
      photoUrl: blockFormData.author.photoUrl || '',
      gender: blockFormData.author.gender || Gender.Male,
    };
    if (!isAuthorEmpty(author)) {
      authorData = author;
    }
  }

  const blockRequestData: Partial<UpsertExploreBlockRequest> = {
    title: blockFormData.title,
    type: blockFormData.type,
    imageUrl:
      blockFormData.imageUrl && blockFormData.imageUrl.trim() !== '' ? blockFormData.imageUrl : '',
    showFeedback: blockFormData.showFeedback,
    status: blockFormData.status,
    personalizations: blockFormData.personalizations,
    language: blockFormData.language,
  };

  if (blockFormData.type === ExploreBlockType.Article && 'generateAudio' in blockFormData) {
    blockRequestData.generateAudio = blockFormData.generateAudio ?? true;
  }

  if ('intro' in blockFormData && blockFormData.intro && blockFormData.intro.trim() !== '') {
    blockRequestData.intro = blockFormData.intro;
  }

  if (parsedContent && !isEmptyValue(parsedContent)) {
    blockRequestData.content = parsedContent;
  }

  if (authorData) {
    blockRequestData.author = authorData;
  }

  if (parsedDetails !== undefined) {
    blockRequestData.details = parsedDetails;
  }

  if (
    'videoUrl' in blockFormData &&
    blockFormData.videoUrl &&
    blockFormData.videoUrl.trim() !== ''
  ) {
    blockRequestData.videoUrl = blockFormData.videoUrl;
  }

  return blockRequestData as UpsertExploreBlockRequest;
};
